/**
 * Konflikt-Resolution Dialog
 * Zeigt Konflikte beim Offline-Sync und ermöglicht manuelle Auflösung
 */

import { useState } from 'react';
import {
  Modal,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Alert,
  Tabs,
  Paper,
  Badge,
  ScrollArea,
  Divider,
  ThemeIcon,
  Center,
  Loader,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconRefresh } from '@tabler/icons-react';
import { useConflicts, useSync } from '../hooks/useSync';
import { PendingSyncItem } from '../lib/syncQueue';
import styles from './ConflictResolutionDialog.module.css';

interface ConflictViewerProps {
  conflict: PendingSyncItem;
  onKeepLocal: (resolvedData: Record<string, any>) => void;
  onKeepServer: () => void;
  isResolving: boolean;
}

function ConflictViewer({ conflict, onKeepLocal, onKeepServer, isResolving }: ConflictViewerProps) {
  const [selectedResolution, setSelectedResolution] = useState<'local' | 'remote' | null>(null);

  const handleResolve = () => {
    if (selectedResolution === 'local') onKeepLocal(conflict.data);
    else if (selectedResolution === 'remote') onKeepServer();
  };

  return (
    <Stack gap="md">
      <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
        <Text size="sm">
          Dieser Bericht wurde an anderer Stelle geändert, während Änderungen von diesem Gerät noch nicht
          übertragen waren.
        </Text>
      </Alert>

      <div>
        <Text fw={600} mb="xs">
          Submission ID: {conflict.submissionId}
        </Text>
        <Badge color="orange">{conflict.formType}</Badge>
      </div>

      <Divider label="Ihre lokalen Änderungen" />

      <Paper p="sm" withBorder className={styles.dataViewer}>
        <ScrollArea>
          <pre style={{ margin: 0, fontSize: '12px' }}>
            {JSON.stringify(conflict.data, null, 2)}
          </pre>
        </ScrollArea>
      </Paper>

      <Divider />

      <Group grow>
        <Button
          variant={selectedResolution === 'local' ? 'filled' : 'light'}
          color="green"
          leftSection={<IconCheck size={16} />}
          onClick={() => setSelectedResolution('local')}
        >
          Meine Änderungen behalten
        </Button>
        <Button
          variant={selectedResolution === 'remote' ? 'filled' : 'light'}
          color="blue"
          leftSection={<IconRefresh size={16} />}
          onClick={() => setSelectedResolution('remote')}
        >
          Stand vom Server behalten
        </Button>
      </Group>

      {selectedResolution && (
        <Text size="xs" c="dimmed">
          {selectedResolution === 'local'
            ? 'Der Stand auf dem Server wird mit den Änderungen von diesem Gerät überschrieben.'
            : 'Die Änderungen von diesem Gerät werden verworfen.'}
        </Text>
      )}

      <Button
        fullWidth
        color={selectedResolution === 'remote' ? 'blue' : 'green'}
        onClick={handleResolve}
        disabled={!selectedResolution || isResolving}
        loading={isResolving}
      >
        Übernehmen
      </Button>
    </Stack>
  );
}

/**
 * Dialog für Konflikt-Resolution
 */
export function ConflictResolutionDialog() {
  const conflicts = useConflicts();
  const { resolveConflict, discardLocal } = useSync();
  const [resolving, setResolving] = useState<string | null>(null);

  if (conflicts.length === 0) {
    return null;
  }

  const currentConflict = conflicts[0];
  const isResolving = resolving === currentConflict.submissionId;

  const handleResolve = async (resolvedData: Record<string, any>) => {
    setResolving(currentConflict.submissionId);
    try {
      resolveConflict(currentConflict.submissionId, resolvedData);
      // Wait a moment for state to update
      await new Promise((resolve) => setTimeout(resolve, 500));
    } finally {
      setResolving(null);
    }
  };

  const handleKeepServer = async () => {
    setResolving(currentConflict.submissionId);
    try {
      discardLocal(currentConflict.submissionId);
      await new Promise((resolve) => setTimeout(resolve, 500));
    } finally {
      setResolving(null);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={() => {
        // Can't close - must resolve
      }}
      title={`${conflicts.length} Sync-Konflikt${conflicts.length > 1 ? 'e' : ''}`}
      size="lg"
      centered
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {/* Konflikt-Übersicht */}
        {conflicts.length > 1 && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
            <Text size="sm">
              {conflicts.length} Submissions haben Konflikte. Lösen Sie diese einzeln auf.
            </Text>
          </Alert>
        )}

        {/* Current Conflict */}
        <Tabs defaultValue="current" key={currentConflict.submissionId}>
          <Tabs.List>
            <Tabs.Tab value="current">
              {currentConflict.submissionId.slice(0, 8)}... ({currentConflict.formType})
            </Tabs.Tab>
            {conflicts.length > 1 && (
              <Tabs.Tab value="all" rightSection={<Badge size="sm">{conflicts.length}</Badge>}>
                Alle
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="current" pt="md">
            <ConflictViewer
              conflict={currentConflict}
              onKeepLocal={handleResolve}
              onKeepServer={handleKeepServer}
              isResolving={isResolving}
            />
          </Tabs.Panel>

          {conflicts.length > 1 && (
            <Tabs.Panel value="all" pt="md">
              <Stack gap="md">
                {conflicts.map((conflict) => (
                  <Paper key={conflict.submissionId} p="sm" withBorder>
                    <Group justify="space-between" mb="xs">
                      <div>
                        <Text size="sm" fw={600}>
                          {conflict.submissionId.slice(0, 12)}...
                        </Text>
                        <Text size="xs" c="dimmed">
                          {conflict.formType}
                        </Text>
                      </div>
                      {resolving === conflict.submissionId ? (
                        <Loader size="sm" />
                      ) : (
                        <IconAlertTriangle color="var(--mantine-color-red-6)" size={20} />
                      )}
                    </Group>
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      fullWidth
                      onClick={() => handleResolve(conflict.data)}
                      disabled={resolving !== null}
                    >
                      Meine Änderungen behalten
                    </Button>
                  </Paper>
                ))}
              </Stack>
            </Tabs.Panel>
          )}
        </Tabs>

        <Divider />

        <Group justify="space-between" c="dimmed">
          <Text size="xs">
            💡 Konflikte entstehen, wenn Änderungen von mehreren Geräten gleichzeitig gemacht werden.
          </Text>
        </Group>
      </Stack>
    </Modal>
  );
}
